import Hierarchy from '@antv/hierarchy'
import 'd3-transition'
import type { WritableDraft } from 'immer/dist/internal'
import { findIndex, isUndefined, merge, pick } from 'lodash-es'
import { Observable } from 'rxjs'
import type { GraphStoreContext, TreeGraphStoreContext } from '../../store'
import { Graph } from '../../entities'
import type {
  AbstractGraph,
  DraftTreeGraphData,
  ElementIdentifier,
  LayoutTreeGraphData,
} from '../../types'
import {
  EdgeConfig,
  EVENT_NAME,
  NodeConfig,
  TreeGraphData,
  TreeGraphLayoutConfig,
  TreeGraphLayoutType,
  TreeGraphOptions,
  TreeNodeData,
} from '../../types'
import { TreeEdge } from './tree-edge'
import { TreeNode } from './tree-node'
import { traverseTree } from './utils'

export class TreeGraph
  extends Graph<TreeNode, TreeEdge>
  implements AbstractGraph
{
  getNodeConfig?: (node: TreeNode) => NodeConfig
  getEdgeConfig?: (edge: TreeEdge) => EdgeConfig

  constructor(options: TreeGraphOptions) {
    super(
      merge(
        {
          layout: {
            type: TreeGraphLayoutType.MindMap,
            direction: 'H',
            getHeight() {
              return 32
            },
            getHGap() {
              return 80
            },
            getVGap() {
              return 20
            },
          },
          defaultNode: {
            type: 'tree-graph-node',
          },
          defaultEdge: {
            type: 'tree-graph-edge',
          },
        },
        options
      ),
      {
        graphType: 'tree',
      }
    )

    this.getNodeConfig = options.getNodeConfig
    this.getEdgeConfig = options.getEdgeConfig

    this.once(EVENT_NAME.afterinit, () => {
      this.service.send({
        type: 'SET_LAYOUT_CONTEXT',
        data: {
          layoutAlgorithm: this.execute,
          modelFactory: this.modelFactory,
        },
      })

      if (options.data) {
        this.changeData(options.data)
      }
    })
  }

  static traverse(
    treeData: TreeNodeData | TreeNodeData[],
    callback: CallableFunction
  ) {
    traverseTree(treeData instanceof Array ? treeData : [treeData], callback)
    return treeData
  }

  /**
   * 设置数据
   */
  changeData = (data: TreeGraphData, stack = true): void => {
    this.service.send({
      type: 'CHANGE_DATA',
      data: {
        graphData: data,
      },
    })

    // 数据改变后自动布局
    this.layout()
  }

  /**
   * 遍历树图数据
   *
   * @param callback 遍历节点执行的回调函数
   * @param target 指定的节点开始查找
   */
  traverse(callback: CallableFunction, target?: TreeNodeData): void {
    traverseTree([target || (this.data as TreeNodeData)], callback)
  }

  /**
   * 查找节点数据
   *
   * @param id 指定的元素 ID
   * @param target 从指定的节点开始查找，为空时从根节点开始查找
   *
   * @return node 查找到的节点数据
   */
  findDataById(
    id: TreeNodeData['id'],
    target?: TreeNodeData
  ): TreeNodeData | null {
    let nodeData: TreeNodeData | null = null

    this.traverse((node: TreeNodeData) => {
      if (node.id === id) {
        nodeData = node
        return false
      }
    }, (target || this.data) as TreeNodeData)

    return nodeData
  }

  /**
   * 添加子节点
   *
   * @param data
   * @param parent
   */
  addChild = (data: TreeNodeData, parent: TreeNodeData): void => {
    this.service.send({
      type: 'ADD_NODE',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as TreeGraphStoreContext

          if (context.graphData) {
            this.traverse((node: TreeNodeData) => {
              if (parent.id === node.id) {
                node.children = node.children || []
                node.children.push(data)

                return false
              }
            }, context.graphData)
          }
        },
      },
    })
  }

  /**
   * 收起节点
   *
   * @param node
   */
  toggleNodeCollapse(node: TreeNode): void {
    this.service.send({
      type: 'UPDATE_NODE',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as TreeGraphStoreContext

          if (context.graphData) {
            this.traverse((item: TreeNodeData) => {
              if (node.id === item.id) {
                item.collapsed = !item.collapsed

                return false
              }
            }, context.graphData)
          }
        },
        eventName: EVENT_NAME.itemcollapsed,
      },
    })
  }

  /**
   * 折叠或展开根节点左侧
   *
   * @param collapsed
   */
  toggleRootNodeLeftSideCollapse(collapsed?: boolean): void {
    this.service.send({
      type: 'UPDATE_NODE',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as TreeGraphStoreContext

          if (context.graphData) {
            context.graphData.leftCollapsed = isUndefined(collapsed)
              ? !context.graphData.leftCollapsed
              : collapsed
          }
        },
        eventName: EVENT_NAME.itemcollapsed,
      },
    })
  }

  /**
   * 折叠或展开根节点右侧
   *
   * @param collapsed
   */
  toggleRootNodeRightSideCollapse(collapsed?: boolean): void {
    this.service.send({
      type: 'UPDATE_NODE',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as TreeGraphStoreContext

          if (context.graphData) {
            context.graphData.rightCollapsed = isUndefined(collapsed)
              ? !context.graphData.rightCollapsed
              : collapsed
          }
        },
        eventName: EVENT_NAME.itemcollapsed,
      },
    })
  }

  /**
   * 更新某个节点下的所有子节点
   * @param data 子树数据模型集合
   * @param parentId
   */
  updateChildren(data: TreeNodeData[], parentId: TreeNodeData['id']): void {
    this.service.send({
      type: 'UPDATE_NODE',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as TreeGraphStoreContext

          if (context.graphData) {
            const parentNode = this.findDataById(parentId, context.graphData)

            if (!parentId || !parentNode) {
              console.warn(
                `Update children failed! There is no node with id '${parentId}'`
              )

              return
            }

            this.traverse((item: TreeNodeData) => {
              if (parentId === item.id) {
                item.children = data

                return false
              }
            }, context.graphData)
          }
        },
      },
    })
  }

  /**
   * 更新源数据，差量更新子树
   * @param nodeData 子树数据模型
   * @param parentId 子树的父节点id
   */
  updateChild(nodeData: TreeNodeData, parentId: TreeNodeData['id']): void {
    this.service.send({
      type: 'UPDATE_NODE',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as TreeGraphStoreContext

          if (context.graphData) {
            const current = this.findDataById(nodeData.id, context.graphData)
            const parentNode = this.findDataById(parentId, context.graphData)

            if (!parentId || !parentNode) {
              console.warn(
                `Update children failed! There is no node with id '${parentId}'`
              )

              return
            }

            if (!parentNode.children) {
              // 当 current 不存在时，children 为空数组
              parentNode.children = []
            }

            // 如果不存在该节点，则添加
            if (!current) {
              parentNode.children.push(nodeData)
            } else {
              const index = findIndex(
                parentNode.children,
                ({ id }: TreeNodeData) => id === nodeData.id
              )

              parentNode.children[index] = nodeData
            }
          }
        },
      },
    })
  }

  /**
   * 删除子树
   * @param id 子树根节点id
   */
  removeChild(id: TreeNodeData['id']): void {
    this.service.send({
      type: 'REMOVE_NODE',
      data: {
        producer: (_context: WritableDraft<GraphStoreContext>) => {
          const context = _context as TreeGraphStoreContext

          if (context.graphData) {
            this.traverse(
              (
                node: TreeNodeData,
                parentNode?: TreeNodeData,
                ...rest: [TreeNodeData[], number]
              ) => {
                if (node.id === id && parentNode) {
                  parentNode.children?.splice(rest[1], 1)

                  return false
                }
              },
              context.graphData
            )
          }
        },
      },
    })
  }

  /**
   * 执行树图布局
   *
   * @param data
   * @returns
   */
  private execute = (
    data: DraftTreeGraphData
  ): Observable<LayoutTreeGraphData> =>
    new Observable<LayoutTreeGraphData>((subscriber) => {
      try {
        const layoutData: LayoutTreeGraphData = Hierarchy[
          this.layoutConfig.type
        ](this.filterRootNodeChildren(data), this.layoutConfig)

        this.traverse((node: TreeNodeData) => {
          Object.assign(
            node,
            {
              width: node.width! - (node.hgap || 0) * 2,
              height: node.height! - (node.vgap || 0) * 2,
            },
            pick(node.data ?? {}, ['type', 'label', 'collapsed'])
          )
        }, layoutData)

        subscriber.next(layoutData)
        subscriber.complete()
      } catch (error) {
        subscriber.error(error)
      }
    })

  /**
   * 过滤被折叠的节点
   *
   * @param rootNode
   * @private
   */
  private filterRootNodeChildren(rootNode: DraftTreeGraphData) {
    const data = { ...rootNode }
    const getSide =
      (this.layoutConfig as TreeGraphLayoutConfig).getSide ||
      (({ data }: TreeNode) => data?.side)
    const { leftCollapsed = false, rightCollapsed = false, children } = data

    if (leftCollapsed || rightCollapsed) {
      data.children = children?.filter((node) => {
        const side = getSide({ data: node } as unknown as TreeNode)

        if (side === 'left' && leftCollapsed) {
          return false
        }

        if (side === 'right' && rightCollapsed) {
          return false
        }

        return true
      })
    }

    return data
  }

  /**
   * 创建模型
   *
   * @param layoutData
   */
  private modelFactory = (layoutData: LayoutTreeGraphData) => {
    const [nodes, positions] = this.createNodesAndPosition(layoutData)
    const edges = this.createEdges(layoutData, nodes)

    return {
      nodes,
      edges,
      positions,
    }
  }

  /**
   * 创建节点
   *
   * @param layoutData
   */
  private createNodesAndPosition(
    layoutData: LayoutTreeGraphData
  ): [TreeNode[], Record<ElementIdentifier, [number, number]>] {
    const nodes: TreeNode[] = []
    const positions: Record<ElementIdentifier, [number, number]> = {}

    if (layoutData) {
      traverseTree([layoutData], (node: TreeNode) => {
        nodes.push(new TreeNode(this, node))
        positions[node.id] = [node.x, node.y]
      })
    }

    return [nodes, positions]
  }

  /**
   * 创建边
   *
   * @param layoutData
   */
  private createEdges(
    layoutData: LayoutTreeGraphData,
    nodes: TreeNode[]
  ): TreeEdge[] {
    const edges: TreeEdge[] = []

    if (layoutData) {
      traverseTree([layoutData], (node: TreeNode) => {
        node.children?.forEach((child) => {
          const edgeModel = new TreeEdge(
            this,
            {
              label: '',
              id: `${node.id}-${child.id}`,
              source: node.id,
              target: child.id,
              layoutDirection: (this.layoutConfig as TreeGraphLayoutConfig)
                .direction,
            },
            nodes
          )

          if (this.getEdgeConfig) {
            Object.assign(edgeModel, this.getEdgeConfig(edgeModel))
          }

          edges.push(edgeModel)
        })
      })
    }

    return edges
  }
}
